import * as stylex from '@stylexjs/stylex';
import { FC } from 'react';
import { styles } from '../../styles';
import { ChildIcon } from '@shared/assets/icons/child';
import { Ticket } from '@pages/event/api/useFetchTicketSegment';
import { ParticipantCardNoSegment } from '../../index';
import { EventSettings } from '@features/api/use-get-settings.ts';

interface GuestsTreeProps {
  ticket: Ticket;
  checkedUsers?: { [key: number]: number };
  toggleUser?: (ticket: Ticket) => void;
  handleDelete?: (ticket: Ticket) => Promise<void> | void;
  eventId?: number;
  refetchSegments?: () => void;
  refetchCategories?: () => void;
  eventDateEnd?: string;
  eventDateStart?: string;
  eventSettings?: EventSettings | null;
}

const formatGuestName = (firstName?: string | null, lastName?: string | null): string => {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Данные отсутствуют';
};

const getGuestChecked = (guestId: number, checkedUsers?: { [key: number]: number }) => {
  return !!checkedUsers?.[guestId];
};

export const GuestsTree: FC<GuestsTreeProps> = ({
  ticket,
  checkedUsers,
  toggleUser,
  handleDelete,
  eventId,
  refetchSegments,
  refetchCategories,
  eventDateEnd,
  eventDateStart,
  eventSettings,
}) => {
  if (!ticket.dependents) {
    return null;
  }

  return (
    <div {...stylex.props(styles.guestsTreeContainer)}>
      <div {...stylex.props(styles.guestsTreeLine)}>
        {Boolean(ticket.dependents.adult?.length) && (
          <div {...stylex.props(styles.guestSection)}>
            <div {...stylex.props(styles.guestSectionTitle)}>Взрослые гости</div>
            <div {...stylex.props(styles.guestsTreeChildren)}>
              {ticket.dependents.adult.map((adult) => {
                const guestName = formatGuestName(adult.participant.firstName, adult.participant.lastName);
                const isDisabled = !adult.participant.active;
                return (
                  <div key={adult.id} {...stylex.props(styles.guestCardRow)}>
                    <ParticipantCardNoSegment
                      ticket={adult}
                      name={guestName}
                      position={adult.participant.position || undefined}
                      checked={getGuestChecked(adult.id, checkedUsers)}
                      disabled={isDisabled}
                      onToggle={() => toggleUser?.(adult)}
                      onDelete={() => handleDelete?.(adult)}
                      eventId={eventId}
                      refetchSegments={refetchSegments}
                      refetchCategories={refetchCategories}
                      eventDateEnd={eventDateEnd}
                      eventDateStart={eventDateStart}
                      toggleUser={toggleUser}
                      handleDelete={handleDelete}
                      checkedUsers={checkedUsers}
                      isGuest={true}
                      eventSettings={eventSettings}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {Boolean(ticket.dependents.child?.length) && (
          <div {...stylex.props(styles.guestSection)}>
            <div {...stylex.props(styles.guestSectionTitle)}>
              Маленькие гости
              <ChildIcon />
            </div>
            <div {...stylex.props(styles.guestsTreeChildren)}>
              {ticket.dependents.child.map((child) => {
                const guestName = formatGuestName(child.participant.firstName, child.participant.lastName);
                const isDisabled = !child.participant.active;
                return (
                  <div key={child.id} {...stylex.props(styles.guestCardRow)}>
                    <ParticipantCardNoSegment
                      ticket={child}
                      name={guestName}
                      position={child.participant.position || undefined}
                      checked={getGuestChecked(child.id, checkedUsers)}
                      disabled={isDisabled}
                      onToggle={() => toggleUser?.(child)}
                      onDelete={() => handleDelete?.(child)}
                      eventId={eventId}
                      refetchSegments={refetchSegments}
                      refetchCategories={refetchCategories}
                      eventDateEnd={eventDateEnd}
                      eventDateStart={eventDateStart}
                      toggleUser={toggleUser}
                      handleDelete={handleDelete}
                      checkedUsers={checkedUsers}
                      isGuest={true}
                      eventSettings={eventSettings}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
